// WebAuthn / passkey registration error classification.
//
// When the user already has a credential for this RP on the authenticator,
// `navigator.credentials.create` (via @simplewebauthn/browser) throws
// `InvalidStateError: The authenticator was previously registered`. The
// server also returns 409 "This passkey is already registered on your
// account" if finish races a prior ceremony. Both are expected UX, not
// Priority Fixes — stamp `sentryExpected` so TanStack Query's mutation
// hook (see `isExpectedHttpError` in queryClient) does not report them.

export function isPasskeyAlreadyOnDeviceError(err: unknown): boolean {
  if (err && typeof err === "object" && "name" in err) {
    if ((err as { name?: string }).name === "InvalidStateError") return true;
  }
  const msg = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  return msg.includes("previously registered") || msg.includes("already registered");
}

export function isPasskeyCancelledError(err: unknown): boolean {
  if (err && typeof err === "object" && "name" in err) {
    const name = String((err as { name?: string }).name ?? "");
    if (name === "NotAllowedError" || name === "AbortError") return true;
  }
  const msg = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  return (
    msg.includes("cancel") ||
    msg.includes("abort") ||
    msg.includes("not allowed") ||
    msg.includes("notallowed")
  );
}

/** Rethrow, marking already-on-device errors so they skip Sentry. */
export function rethrowPasskeyRegistrationError(err: unknown): never {
  if (isPasskeyAlreadyOnDeviceError(err)) {
    const error = err instanceof Error ? err : new Error(String(err));
    (error as { sentryExpected?: boolean }).sentryExpected = true;
    throw error;
  }
  throw err;
}
